import {Component} from '@angular/core';
import {FilterChangedEvent, GridApi, SelectionColumnDef, ViewportChangedEvent} from "@ag-grid-community/core";
import {pageContext, tableColumns} from "@pages/system/client-portal/page-activity/page-activity.component.ds"
import {TableBaseComponent} from "@pages/table.base.component";
import {KpiSidebarItem} from "@shared/ag-kpi-sidebar/ag-kpi-sidebar.component.ds";
import {ActivatedRoute} from "@angular/router";
import {Activity, ActivityLineChartData} from "@data/interefaces/client-portal.interfaces";
import {PageActivityService} from "@data/client-portal/page-activity.service";
import {TimeUnit} from "@data/enums/data.enums"
import {AgGridService} from "@shared/services/ag-grid.service";
import {FeatureComponentMode} from "@core/enums/core.enums";
import {ClientPortalActivityService} from "@data/client-portal/client-portal-activity.service";

@Component({
  selector: 'app-page-activity',
  templateUrl: './page-activity.component.html',
  styleUrls: ['./page-activity.component.scss'],
  providers: [AgGridService]
})

export class PageActivityComponent extends TableBaseComponent {
  currentMode: string = FeatureComponentMode.DATA;
  scale: TimeUnit = TimeUnit.HOUR;

  refreshComplete: boolean = true;
  isFilterActive: boolean = false;
  rowData: Activity[] = [];
  kpiSidebar: KpiSidebarItem[] = [];

  chartOptions: any;
  dataTypeOptions: any;
  chartTitle = `Page Activity Charts`;
  selectedDataType: string = 'newSessions';
  page:string = 'pageActivity';

  protected override readonly tableColumns = tableColumns;
  protected readonly pageContext = pageContext;
  private readonly pageContextNonTableFilterParameters=['mode','scale'];

  constructor(private _activatedRoute: ActivatedRoute, private _pageActivityService: PageActivityService, private _clientPortalActivityService: ClientPortalActivityService) {
    super();
  }

  public override ngOnInit() {
    super.ngOnInit();
    this.initiatePageState(this._activatedRoute.snapshot.queryParamMap);
    if(this.pageContext.mode) this.switchMode(this.pageContext.mode);
    if(this.pageContext.scale) this.initiateScaleValue(this.pageContext.scale);
    this.dataTypeOptions = this._clientPortalActivityService.getChartDataTypesFor(this.page);
    this.refreshView();
  }

  switchMode(mode: string): void {
    this.currentMode = mode;
    this.pageContext.mode = mode;
    this.updateToPageContextUrl();
    if (!this.isDataMode()) {
      this.loadChartData();
    }
  }

  initiateScaleValue(scaleStr:string){
    let scaleUnit = Object.values(TimeUnit)
      .filter(value => value != TimeUnit.SECOND && value.toString() == scaleStr)[0];
    if(scaleUnit) this.onScaleChanged(scaleUnit);
  }

  isDataMode(): boolean {
    return this.currentMode.toLowerCase() === 'data';
  }

  private loadChartData() {
    if (this.isGridGrouped()) {
      this.loadChartDataWithGroup();
    } else {
      this.loadChartDataWithoutGroup();
    }
  }

  refreshView() {
    this.refreshComplete = false;
    this.displayProgressBar(true);
    this._pageActivityService.getAllPageActivity()
      .then(result => {
        this.rowData = this._clientPortalActivityService.convertResults(result);
      })
      .catch(error => this.displayNotificationMessage('error', JSON.stringify(error)))
      .finally(() => {
        this.refreshComplete = true;
        this.displayProgressBar(false)
      });
  }

  onFilterChanged(event: FilterChangedEvent<any>) {
    const updateLiveDataFilter = this.isUpdateLiveDataFilterRequired(event.columns);
    this._agGridService.processOnFilterChanged(event, this.pageContext, this.updatePageContext.bind(this)).then(() => {
      if (updateLiveDataFilter) {
        this.onLiveDataFilterChange(true);
      }
    });
  }

  onLiveDataFilterChange(forceReload: boolean = false): void {
    this.updateToPageContextUrl();
  }

  onScaleChanged(selectedValue: TimeUnit): void {
    this.scale = selectedValue;
    this.pageContext.scale = selectedValue.toString();
    this.updateToPageContextUrl();
    this.loadComponentsData();
  }

  onDataTypeChange(selectedValue: string): void {
    this.selectedDataType = selectedValue;
    this.loadChartData()
  }

  protected override getSelectionColumnDef(): SelectionColumnDef {
    return {};
  }

  private getFilteredData(): Activity[] {
    return this.getRowsAfterFilter().filter(row => !!row);
  }

  private loadComponentsData() {
    this.loadKpiMetrics();
    if (!this.isDataMode()) {
      this.loadChartData();
    }
  }

  private loadKpiMetrics() {
    this.kpiSidebar = this._pageActivityService.getPageActivityKpi(this.getFilteredData(), this.scale);
  }

  protected override updatePageContext(updateContextUrl: boolean = true): void {
    this.loadComponentsData();
  }

  protected override onViewportChanged(event: ViewportChangedEvent<any>) {
    this.loadComponentsData();
  }

  protected override updateAgGridFilters(pageContext: any, api: GridApi) {
    this._agGridService.updateAgGridFiltersHandler(pageContext, api, this.pageContextNonTableFilterParameters);
  }

  // Visualizations
  isGridGrouped(): boolean {
    const rowGroupColumns = this.gridApi?.getRowGroupColumns();
    return rowGroupColumns?.length > 0;
  }

  private loadChartDataWithoutGroup() {
    const chartData: ActivityLineChartData[] = this._clientPortalActivityService.getActivityLineChartData(this.getFilteredData(), this.scale, this._clientPortalActivityService.CHART_DATA_AMOUNT);
    this.chartOptions = this._clientPortalActivityService
      .getChartOptionsWithoutGroups(chartData,this.scale,this.chartTitle, this.page);
  }

  private loadChartDataWithGroup() {
    const groupedChartData = this._clientPortalActivityService.getGroupedActivityLineChartData(this.getGroupFilteredData(), this.scale, this._clientPortalActivityService.CHART_DATA_AMOUNT);
    this.chartOptions = this._clientPortalActivityService
      .getChartOptionsWithGroup(groupedChartData, this.scale, this.selectedDataType, this.page);
  }

  private getGroupFilteredData(): Map<string, Activity[]> {
    const groupMap: Map<string, Activity[]> = new Map();
    if (this.gridApi && !this.gridApi.isDestroyed()) {
      this.gridApi.forEachNodeAfterFilter((node: any) => {
        if (node.group && node.level === 0 && node.key) {
          const groupValue = node.key;
          groupMap.set(groupValue, []);
          this._clientPortalActivityService.collectRowsUnderGroup(node, groupMap.get(groupValue)!);
        }
      });
    }
    return groupMap;
  }
}
